import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { AuthSessionContext } from '@context/AuthContext'

import { Button } from '@ui/components/Button'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { getCompanyFullName } from '@utils/companyName.utils'
import { CommonPaths } from '@utils/path'

import { Loading } from '@shared/components/Loading/Loading'
import {
  StatusTemplateBody,
  StatusTemplateButtons,
  StatusTemplateWrapper,
} from '@shared/templates/StatusTemplate/StatusTemplate'

export const DocumentVerificationPage: React.FunctionComponent = () => {
  const apiClient = useApiClient(ClientApiClient)

  const { data: documents = [], isLoading } = useQuery({
    queryKey: queryKeys.document.required(),
    queryFn: () => apiClient.getRequiredDocuments(),
  })

  return (
    <Loading isLoading={isLoading} showLoadingIcon>
      {documents.length ? <VerificationPending /> : <VerificationProgress />}
    </Loading>
  )
}

const VerificationPending = () => {
  const { t } = useTranslation()

  return (
    <StatusTemplateWrapper>
      <StatusTemplateBody
        title={t('Profile.Verification pending')}
        text={[
          t(
            "Profile.Please wait for Tickmill's approval before you can start trading. It takes max 1 workday after you submitted documents."
          ),
        ]}
        icon={'Pending'}
      />
    </StatusTemplateWrapper>
  )
}

const VerificationProgress = () => {
  const navigate = useNavigate()

  const [session] = useContext(AuthSessionContext)

  const { t } = useTranslation()

  const handleUploadDocuments = () => {
    navigate(CommonPaths.DocumentManagement)
  }

  return (
    <StatusTemplateWrapper>
      <StatusTemplateBody
        title={t('Profile.Verification in progress')}
        text={[
          `${t('Profile.Thank you for registering with')} ${getCompanyFullName(
            session?.tickmillCompany.id
          )}.`,
          t(
            'Profile.Please allow us some time to verify your documents. You will be notified by email as soon as the verification process is complete.'
          ),
        ]}
        icon={'Pending'}
      />
      <StatusTemplateButtons>
        <Button.Primary size='lg' onClick={handleUploadDocuments}>
          {t('Profile.Upload Documents')}
        </Button.Primary>
      </StatusTemplateButtons>
    </StatusTemplateWrapper>
  )
}
